/** Building blocks that only the billing screens use (Figma "Billing — …" frames). */
import { CheckCircle2, ChevronLeft } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Badge, cx } from "@/components/ui";
import type { InvoiceStatus } from "@/lib/types";

export function BackLink({ to, children = "Back" }: { to: string; children?: ReactNode }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 self-start text-sm text-fg-subtle hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden /> {children}
    </Link>
  );
}

export function PageTitle({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <h1 className="text-[30px] leading-9 font-semibold text-fg">{title}</h1>
      {subtitle && <p className="text-sm text-[#766e6a]">{subtitle}</p>}
    </div>
  );
}

export function SectionTitle({ size = "lg", children }: { size?: "lg" | "md"; children: ReactNode }) {
  return (
    <h2 className={cx("font-semibold text-fg", size === "lg" ? "text-lg leading-7" : "text-base leading-6")}>
      {children}
    </h2>
  );
}

/** The green banner at the top of the "done" screens. */
export function Confirmation({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3.5 rounded-[14px] border border-[#b7e5d1] bg-[#eef8f3] px-5 py-[18px]">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-surface text-[#13734d]">
        <CheckCircle2 className="size-5" aria-hidden />
      </div>
      <div className="min-w-0">
        <p className="text-base leading-6 font-semibold text-fg">{title}</p>
        <p className="mt-[3px] text-sm text-fg-muted">{children}</p>
      </div>
    </div>
  );
}

/** A tile + title + description row with an optional action (Figma "Set the plan to work"). */
export function TaskRow({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3.5 py-[13px]">
      <div className="flex size-[38px] shrink-0 items-center justify-center rounded-[11px] bg-[#faf6ef] text-fg-muted">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">{title}</p>
        <p className="mt-[3px] max-w-[300px] text-xs text-[#766e6a]">{description}</p>
      </div>
      {action}
    </div>
  );
}

export function InvoiceStatusPill({ status }: { status: InvoiceStatus }) {
  if (status === "paid") return <Badge tone="success">Paid</Badge>;
  if (status === "failed") return <Badge tone="danger">Failed</Badge>;
  // A Direct Debit being collected (settled within a few working days).
  if (status === "pending") return <Badge tone="warning">Processing</Badge>;
  // A renewal not paid in time: the plan moved to Free, nothing is owed.
  if (status === "void") return <Badge tone="neutral">Cancelled</Badge>;
  return <Badge tone="neutral">Refunded</Badge>;
}
